import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

type ToastTone = "success" | "danger" | "warning" | "info";
type ToastMsg = { id: number; tone: ToastTone; text: string };
const Ctx = createContext<(tone: ToastTone, text: string) => void>(() => {});
/** Erro fica até o usuário fechar (WCAG 2.2.1); os demais somem sozinhos, com pausa no hover e no foco (FE22). */
const DURATION: Record<ToastTone, number | null> = { success: 5000, info: 6000, warning: 8000, danger: null };

function ToastItem({ t, dismiss }: { t: ToastMsg; dismiss(id: number): void }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const ms = DURATION[t.tone];
    if (ms === null || paused) return;
    const h = setTimeout(() => dismiss(t.id), ms);
    return () => clearTimeout(h);
  }, [t.id, t.tone, paused, dismiss]);
  // text-bg-warning e text-bg-info têm texto escuro: o X branco não teria contraste (1.4.11).
  const lightClose = t.tone === "success" || t.tone === "danger";
  return (
    <div
      className={`toast show align-items-center text-bg-${t.tone} border-0 mb-2`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="d-flex">
        <div className="toast-body">{t.text}</div>
        <button
          type="button"
          className={`btn-close${lightClose ? " btn-close-white" : ""} me-2 m-auto`}
          aria-label="Fechar aviso"
          onClick={() => dismiss(t.id)}
        />
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastMsg[]>([]);
  const dismiss = useCallback((id: number) => setItems((x) => x.filter((t) => t.id !== id)), []);
  const push = useCallback((tone: ToastTone, text: string) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, tone, text }]);
  }, []);
  // Duas regiões vivas fixas e sempre montadas; os itens não têm role (sem regiões aninhadas).
  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="toast-container position-fixed bottom-0 end-0 p-3">
        <div role="status" aria-live="polite">
          {items
            .filter((t) => t.tone !== "danger")
            .map((t) => (
              <ToastItem key={t.id} t={t} dismiss={dismiss} />
            ))}
        </div>
        <div role="alert" aria-live="assertive">
          {items
            .filter((t) => t.tone === "danger")
            .map((t) => (
              <ToastItem key={t.id} t={t} dismiss={dismiss} />
            ))}
        </div>
      </div>
    </Ctx.Provider>
  );
}
export const useToast = () => useContext(Ctx);
